import React, { useContext, useState, useEffect } from 'react';
import { Button } from '@mui/base/Button';
import { ConnectionContext, getAPIImage, displayLoader, getMatchURL } from '../utils';
import { getStoredData } from '../utils/data-pulls';



/**
 * Large Matchbox Component
 */
const MatchBox = (props) => {
    const { 
        data = null, 
        includeLink = true, 
        ...restProps 
    } = props;
    if ( !data ) return;
    const connectionInfo = useContext(ConnectionContext);
    const [ isLoaded, setLoaded ] = useState(false);

    const [ penaltyString, setPenaltyString ] = useState(null);

    const [ homeLink, setHomeLink ] = useState(null);
    const [ homeImage, setHomeImage ] = useState(null);
    const [ homeWon, setHomeWon ] = useState(false);

    const [ awayLink, setAwayLink ] = useState(null);
    const [ awayImage, setAwayImage ] = useState(null);
    const [ awayWon, setAwayWon ] = useState(false);

    // Destructure data
    const {
        awayTeam = {},
        awayTeamResult = {},
        competition = {},
        currentMatchPhase = {},
        currentMinute = '0',
        dateTimeUTC = {},
        facility = {},
        homeTeam = {},
        homeTeamResult = {},
        id = '',
        liveStatus = '',
        ...restData
    } = data;

    /**
     * Get the website page links for teams
     */
    useEffect(() => {

        // Home Link
        if ( homeTeam.hasOwnProperty('id') ) {
            let homeArgs = [
                { 
                    key: 'club',
                    searchBy: 'uid',
                    id: homeTeam.id
                }
            ];
            getStoredData(homeArgs).then(
                (result) => {
                    if ( result.data.club && result.data.club.hasOwnProperty('url') ) {
                        setHomeLink(result.data.club.url);
                    }
                }
            ).catch(
                (error) => {
                    setError(error);
                }
            );
        }

        // Away Link
        if ( awayTeam.hasOwnProperty('id') ) {
            let awayArgs = [
                { 
                    key: 'club',
                    searchBy: 'uid',
                    id: awayTeam.id
                }
            ];
            getStoredData(awayArgs).then(
                (result) => {
                    if ( result.data.club && result.data.club.hasOwnProperty('url') ) {
                        setAwayLink(result.data.club.url);
                    }
                }
            ).catch(
                (error) => {
                    setError(error);
                }
            );
        }

    }, [homeTeam, awayTeam]);

    /**
     * Get images for each team
     */
    useEffect(()=> {

        (async () => {
            try {
                if ( awayTeam && 'picture' in awayTeam ) {
                    setAwayImage(await getAPIImage(awayTeam.picture, connectionInfo));
                }
                if ( homeTeam && 'picture' in homeTeam ) {
                    setHomeImage(await getAPIImage(homeTeam.picture, connectionInfo));
                }
                setLoaded(true);
            } catch (err) {
              console.error(err);
            }
          })();
    }, [connectionInfo]);

    
    /**
     * Set up display values
     */
    useEffect(() => {

        /**
         * Winner Results
         */
        if ( 'PLAYED' == liveStatus ) {
            // Check for penalty wins first
            if ( awayTeamResult.hasOwnProperty('penaltyWin') && awayTeamResult.penaltyWin ) {
                setAwayWon(true);
            } else if ( homeTeamResult.hasOwnProperty('penaltyWin') && homeTeamResult.penaltyWin ) {
                setHomeWon(true);
            } else if ( awayTeamResult.hasOwnProperty('current') && homeTeamResult.hasOwnProperty('current') ) {
                // Check for wins without penalties
                if ( awayTeamResult.current > homeTeamResult.current ) {
                    setAwayWon(true);
                } else if ( awayTeamResult.current < homeTeamResult.current ) {
                    setHomeWon(true);
                } else {
                    setAwayWon(true);
                    setHomeWon(true);
                }
            }
        }

        /**
         * Penalty Goals
         */
        if ( awayTeamResult.hasOwnProperty('penalties') || homeTeamResult.hasOwnProperty('penalties') ) {
            let homePenalty = homeTeamResult.hasOwnProperty('penalties') ? homeTeamResult.penalties : 0;
            let awayPenalty = awayTeamResult.hasOwnProperty('penalties') ? awayTeamResult.penalties : 0;
            setPenaltyString(`(PEN: ${homePenalty}-${awayPenalty})`);
        }


    }, [data]);

    


    return (
        <div className="matchBox">
            <div className="matchBox__inner">

                {/** HOME TEAM */}
                <div className="matchBox__side matchBox__side--home">
                        { homeImage || !isLoaded ? (
                            <div className="matchBox__img__wrapper">
                                <div className="matchBox__img">
                                    { isLoaded ? (<img  src={ homeImage ? homeImage : '' } />) : displayLoader() }
                                </div>
                                { homeLink ? (
                                    <a href={homeLink} className="matchBox__img__link">
                                        <span className="acaScreenReader">See { "name" in homeTeam ? homeTeam.name : ''} page</span>
                                    </a>
                                ) : '' }
                            </div>
                        ) : '' }
                    <div className="matchBox__info">
                        <h4 className="matchBox__name">{ homeTeam && "name" in homeTeam ? homeTeam.name : ''}</h4>
                        {/* <p className="matchBox__position">4th</p> */}
                    </div>
                    <div className="matchBox__score__wrapper">
                        { homeWon ? (<span className="matchBox__star"></span>) : '' }
                        <span className="matchBox__score">{ homeTeamResult && "current" in homeTeamResult ? homeTeamResult.current : '-'}</span>
                    </div>
                </div>

                {/** MATCH PHASE */}
                <div className="matchBox__phase__wrapper">
                    <span className="matchBox__phase">{currentMinute}</span>
                    { penaltyString ? (
                        <span className="matchBox__penalties">{penaltyString}</span>
                    ) : '' }
                </div>

                {/** AWAY TEAM */}
                <div className="matchBox__side matchBox__side--away">
                        { awayImage || !isLoaded ? (
                            <div className="matchBox__img__wrapper">
                                <div className="matchBox__img">
                                    { isLoaded ? (<img  src={ awayImage ? awayImage : '' } />) : displayLoader() }
                                </div>
                                { awayLink ? (
                                    <a href={awayLink} className="matchBox__img__link">
                                        <span className="acaScreenReader">See { "name" in awayTeam ? awayTeam.name : ''} page</span>
                                    </a>
                                ) : '' }
                            </div>
                        ) : '' }
                    <div className="matchBox__info">
                        <h4 className="matchBox__name">{ awayTeam && "name" in awayTeam ? awayTeam.name : ''}</h4>
                        {/* <p className="matchBox__position">4th</p> */}
                    </div>
                    <div className="matchBox__score__wrapper">
                        { awayWon ? (<span className="matchBox__star"></span>) : '' }
                        <span className="matchBox__score">{ awayTeamResult && "current" in awayTeamResult ? awayTeamResult.current : '-'}</span>
                    </div>
                </div>

            </div>

            { includeLink ? (
                <Button className="acaBtn acaBtn--solid acaBtn--black" href={getMatchURL(id)}>See full match details</Button>
            ) : '' }
            
        </div>
    );
}



/**
 * Export
 */
export { MatchBox };